/**
 * CAD → USD display for product/cart prices (US visitors only).
 * Checkout stays CAD.
 *
 * Preserves labels like "From" / "À partir de" and multi-price strings.
 * Each converted text node is remembered, so a node is only re-converted when
 * the theme rewrote it with a fresh CAD amount (never converted twice).
 */
(function () {
    var config = window.ErgonofisUSPricing;
    if (!config || !config.enabled) return;

    var FALLBACK_CAD_TO_USD = 0.71;
    var converting = false;
    var observer = null;
    var ourOutput = typeof WeakMap === "function" ? new WeakMap() : null;
    // Formatted USD strings we already wrote — never convert these again
    // (guards against double conversion when the theme clones/replaces text nodes)
    var emittedUsd = Object.create(null);

    // Trailing CAD/USD is optional so "$345.00 CAD" is matched as one amount.
    // No leading whitespace: it would glue "From" / "•" to the price.
    var MONEY_RE =
        /-?(?:\$|CA\$|US\$)\s*[\d.,]+(?:\s*(?:CAD|USD))?|-?[\d.,]+\s*(?:\$|CAD|USD)/gi;

    var SELECTORS = [
        "[data-product-price]",
        "[data-compare-price]",
        "#product-price-bar",
        ".product-item__price",
        ".product__price--strike",
        ".price .new-price",
        ".price .old-price",
        "span.price",
        ".product__price",
        ".cart-bar [data-product-price]",
        ".cart-bar .product__price--strike",
        ".product-item__price__holder .price",
        '[data-spark="b2c-only"] .price',
        ".product__submit__item .price",
        /* Add to cart button price */
        ".add_to_cart_price",
        "#btn-add-to-cart .add_to_cart_price",
        "[data-add-to-cart] .add_to_cart_price",
        "[data-add-to-cart-bar] .add_to_cart_price",
        ".product__submit__add .add_to_cart_price",
        ".cart-bar__submit .add_to_cart_price",
        /* Cart page / drawer */
        ".cart__total__price",
        ".th_cart_total_price",
        ".th_item_price",
        "[data-cart-total]",
        ".cart__price__holder .cart__total__price",
        ".item__price",
        ".item__price .money",
        ".th_item_price .money",
        ".cart__item .item__price",
        ".cart__item .money",
        ".cart-item__price",
        "[data-cart-item-price]",
        "[data-item-price]",
        "[data-items-holder] .money",
        "[data-cart-drawer] .money",
        "[data-left-to-spend]",
        ".cart__total__discount .cart__total > span:last-child",
        /* Upsell / Pair with */
        "[data-upsell-products] .price",
        "[data-upsell-products] .new-price",
        "[data-upsell-products] [data-product-price]",
        "[data-upsell-widget] .price",
        "[data-upsell-widget] .new-price",
        ".cart__widget__products .price",
        ".cart__widget__products .new-price",
        ".cart__widget__products [data-product-price]",
        ".upsell-product .price",
        ".product-upsell .price",
    ].join(",");

    var SCOPE_SELECTORS = [
        "[data-cart-page]",
        "[data-cart-form]",
        "[data-items-holder]",
        "[data-upsell-products]",
        "[data-upsell-widget]",
        ".cart__items",
        ".cart__aside",
        ".cart-drawer",
        "[data-cart-drawer]",
        "cart-drawer",
        "#cart-drawer",
        /* Product form / ATC button */
        ".product__submit",
        "#btn-add-to-cart",
        "[data-add-to-cart]",
        ".cart-bar__submit",
        "[data-form-wrapper]",
        /* Collection / product grids */
        ".product-grid",
        ".collection",
        "[data-collection-products]",
        "[data-products-grid]",
        ".product-item",
        ".product-item__price__holder",
    ].join(",");

    var PRICE_HOSTS = [
        "[data-product-price]",
        "[data-compare-price]",
        "#product-price-bar",
        ".product__price",
        ".product-item__price",
        ".add_to_cart_price",
        ".new-price",
        ".old-price",
        ".item__price",
        ".th_item_price",
        ".cart__total__price",
        ".th_cart_total_price",
        "[data-cart-total]",
        "[data-items-holder]",
        "[data-cart-drawer]",
    ].join(",");

    var CART_SCOPE =
        "[data-cart-drawer], [data-cart-page], [data-cart-form], [data-items-holder], .cart__items, .cart__aside, .cart-drawer, #cart-drawer";

    var SKIP_SELECTOR =
        "script, style, noscript, [data-us-currency-banner], input, textarea, select, .facet-filters, .facets, [data-facets]";

    function cadToUsdDollars(amountCad) {
        var centsCad = Math.round(Number(amountCad) * 100);
        if (
            typeof Currency !== "undefined" &&
            Currency.convert &&
            Currency.rates &&
            Currency.rates.CAD
        ) {
            return Currency.convert(centsCad, "CAD", "USD") / 100;
        }
        return amountCad * (config.rate || FALLBACK_CAD_TO_USD);
    }

    function formatUsd(amountCad) {
        var dollars = cadToUsdDollars(amountCad);
        var formatted;
        try {
            formatted = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            }).format(dollars);
        } catch (e) {
            formatted = "$" + dollars.toFixed(2);
        }
        // Always show the currency code so cart line vs subtotal stay clear
        // (checkout is still CAD — banner already explains that).
        if (!/\bUSD\b/i.test(formatted)) {
            formatted = formatted.replace(/\u00a0/g, " ").trim() + " USD";
        }
        rememberEmitted(formatted);
        rememberEmitted(formatted.replace(/\s*USD\s*$/i, "").trim());
        return formatted;
    }

    function rememberEmitted(formatted) {
        if (!formatted) return;
        emittedUsd[formatted] = 1;
        emittedUsd[String(formatted).replace(/\u00a0/g, " ").trim()] = 1;
    }

    function alreadyEmitted(text) {
        if (!text) return false;
        var t = String(text).replace(/\u00a0/g, " ").trim();
        return !!(emittedUsd[t] || emittedUsd[t.replace(/\s+/g, " ")]);
    }

    function parseCadAmount(text) {
        if (!text) return null;
        var cleaned = String(text)
            .replace(/\u00a0/g, " ")
            .replace(/CAD|USD|CA\$|US\$/gi, "")
            .replace(/[^\d.,-]/g, "")
            .trim();

        if (!cleaned) return null;

        if (cleaned.indexOf(",") > -1 && cleaned.indexOf(".") > -1) {
            if (cleaned.lastIndexOf(".") > cleaned.lastIndexOf(",")) {
                cleaned = cleaned.replace(/,/g, "");
            } else {
                cleaned = cleaned.replace(/\./g, "").replace(",", ".");
            }
        } else if (cleaned.indexOf(",") > -1 && cleaned.indexOf(".") === -1) {
            var parts = cleaned.split(",");
            if (parts[parts.length - 1].length === 2) {
                cleaned = cleaned.replace(",", ".");
            } else {
                cleaned = cleaned.replace(/,/g, "");
            }
        }

        var value = parseFloat(cleaned);
        return isNaN(value) ? null : value;
    }

    function looksLikeMoney(text) {
        MONEY_RE.lastIndex = 0;
        return MONEY_RE.test(text || "");
    }

    /**
     * Convert every CAD amount in a string, keeping labels and ranges intact.
     * "From $395.00" → "From $280.45"
     * "$196.00 $245.00" → both converted
     */
    function replaceMoneyInText(text) {
        if (!text || !looksLikeMoney(text)) return text;
        MONEY_RE.lastIndex = 0;
        return String(text).replace(MONEY_RE, function (match) {
            var trimmed = match.replace(/\u00a0/g, " ").trim();
            // Already labeled USD, or already one of our emitted USD strings
            if (/USD\s*$/i.test(trimmed) || alreadyEmitted(trimmed)) {
                return match;
            }
            var amount = parseCadAmount(match);
            if (amount === null || amount === 0) return match;
            var negative = amount < 0 || /^-/.test(trimmed);
            return (negative ? "-" : "") + formatUsd(Math.abs(amount));
        });
    }

    function convertTextNode(node) {
        if (!node || node.nodeType !== 3 || !node.nodeValue) return;
        // Untouched since our last write → nothing to do
        if (ourOutput && ourOutput.get(node) === node.nodeValue) return;
        if (!looksLikeMoney(node.nodeValue)) return;

        var parent = node.parentElement;
        if (!parent || parent.closest(SKIP_SELECTOR)) return;

        // Whole node already a USD amount we emitted (e.g. theme cloned the node)
        if (alreadyEmitted(node.nodeValue)) {
            if (ourOutput) ourOutput.set(node, node.nodeValue);
            return;
        }

        var replaced = replaceMoneyInText(node.nodeValue);
        if (replaced === node.nodeValue) return;

        node.nodeValue = replaced;
        if (ourOutput) ourOutput.set(node, replaced);
        parent.setAttribute("data-us-price-converted", "1");
    }

    function convertElement(el) {
        if (!el || el.nodeType !== 1) return;
        if (el.closest(SKIP_SELECTOR)) return;

        var text = (el.textContent || "").trim();
        if (!text || text.length > 120 || !looksLikeMoney(text)) return;

        for (var i = 0; i < el.childNodes.length; i++) {
            convertTextNode(el.childNodes[i]);
        }

        var moneyKids = el.querySelectorAll(
            ".new-price, .old-price, .add_to_cart_price, .th_cart_total_price, .th_item_price, .money, [data-product-price], [data-compare-price]",
        );
        for (var k = 0; k < moneyKids.length; k++) {
            convertElement(moneyKids[k]);
        }
    }

    function convertTextNodesIn(root) {
        var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
        var node;
        while ((node = walker.nextNode())) {
            convertTextNode(node);
        }
    }

    function markPricesReady() {
        document.documentElement.classList.add("us-prices-ready");
    }

    function runConversion(work) {
        if (converting) return;
        converting = true;
        if (observer) observer.disconnect();
        try {
            work();
            markPricesReady();
        } finally {
            converting = false;
            if (observer) {
                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                    characterData: true,
                });
            }
        }
    }

    function convertAll() {
        runConversion(function () {
            var nodes = document.querySelectorAll(SELECTORS);
            for (var i = 0; i < nodes.length; i++) {
                convertElement(nodes[i]);
            }
            var scopes = document.querySelectorAll(SCOPE_SELECTORS);
            for (var s = 0; s < scopes.length; s++) {
                convertTextNodesIn(scopes[s]);
            }
        });
    }

    /**
     * The theme rewrites CAD into the DOM on variant change. MutationObserver
     * callbacks run before the next paint, so converting synchronously here
     * (no setTimeout) means the CAD amount is never actually displayed.
     */
    function convertHostsNow(hosts) {
        if (!hosts.length) return;
        runConversion(function () {
            for (var i = 0; i < hosts.length; i++) {
                convertElement(hosts[i]);
                convertTextNodesIn(hosts[i]);
            }
        });
    }

    function collectHosts(mutations) {
        var hosts = [];
        var cartTouched = false;

        function push(el) {
            if (el && hosts.indexOf(el) === -1) hosts.push(el);
        }

        for (var i = 0; i < mutations.length; i++) {
            var mutation = mutations[i];
            var target =
                mutation.target && mutation.target.nodeType === 1
                    ? mutation.target
                    : mutation.target && mutation.target.parentElement;

            if (target && target.closest) {
                push(target.closest(PRICE_HOSTS));
                if (target.closest(CART_SCOPE)) cartTouched = true;
            }

            for (var a = 0; a < mutation.addedNodes.length; a++) {
                var added = mutation.addedNodes[a];
                if (!added || added.nodeType !== 1) continue;
                if (added.closest && added.closest(CART_SCOPE)) {
                    cartTouched = true;
                }
                if (added.matches && added.matches(CART_SCOPE)) {
                    cartTouched = true;
                    push(added);
                }
                if (added.matches && added.matches(PRICE_HOSTS)) {
                    push(added);
                } else if (
                    added.querySelector &&
                    added.querySelector(PRICE_HOSTS)
                ) {
                    push(added);
                }
                // Cart line items often use .th_item_price / .money only
                if (
                    added.querySelector &&
                    added.querySelector(
                        ".th_item_price, .item__price, .money, [data-cart-total]",
                    )
                ) {
                    push(added);
                    cartTouched = true;
                }
            }
        }
        return { hosts: hosts, cartTouched: cartTouched };
    }

    function whenReady(cb) {
        // Convert immediately with fallback rate — do not wait on currencies.js
        // (waiting caused a long CAD flash / long hidden prices).
        cb();
        // If Currency CDN loads later, we already converted; emittedUsd prevents
        // a second pass from double-converting.
    }

    function boot() {
        whenReady(function () {
            convertAll();

            // Optional refinement once Shopify Currency rates are present
            var tries = 0;
            (function waitCurrency() {
                if (typeof Currency !== "undefined" && Currency.convert) {
                    return;
                }
                if (++tries < 40) setTimeout(waitCurrency, 50);
            })();

            observer = new MutationObserver(function (mutations) {
                if (converting) return;
                var result = collectHosts(mutations);
                // Cart AJAX often injects line prices outside PRICE_HOSTS —
                // force a full pass so line + subtotal stay in sync.
                if (result.cartTouched) {
                    convertAll();
                    return;
                }
                convertHostsNow(result.hosts);
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
                characterData: true,
            });

            document.addEventListener("change", function (event) {
                var t = event.target;
                if (!t || !t.matches) return;
                if (
                    t.matches(
                        '[data-single-option-selector], [name="id"], select, input[type="radio"], [name="updates[]"], .quantity__input',
                    )
                ) {
                    setTimeout(convertAll, 250);
                }
            });

            [
                "cart:updated",
                "theme:cart:change",
                "theme:cart",
                "drawer:open",
            ].forEach(function (evt) {
                document.addEventListener(evt, function () {
                    setTimeout(convertAll, 250);
                });
            });

            setTimeout(markPricesReady, 2000);
        });
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
})();
